import { CircularProgress, Grid, Typography } from '@mui/material';
import { grey } from '@mui/material/colors';
import { useFormatForecastData } from 'features/forecasts/hooks/useFormatForecastData';
import { useLocation, useNavigate } from 'react-router-dom';
import { useLocationForWeatherWindow } from './api/getLocationForWeatherWindow';
import { useWeatherWall } from './api/getWeatherWall';
import WeatherChart from './WeatherChart';

interface Props {
  row: number;
  column: number;
  wallId: number;
}

const WeatherWindow = ({ row, column, wallId }: Props) => {
  const navigate = useNavigate();
  const pathName = useLocation().pathname;
  const inEditMode = pathName.includes('edit-weather');
  const { data: wall } = useWeatherWall({ wallId });

  const thisWindow = wall?.weatherWindows?.find(
    (weatherWindow) =>
      weatherWindow.pos_x === row && weatherWindow.pos_y === column
  );
  const { isLoading, data: location } = useLocationForWeatherWindow({
    windowId: thisWindow?.id,
    config: { refetchInterval: 300000 },
  });

  const handleWindowClicked = () => {
    if (!inEditMode && thisWindow) navigate(`/weather-window/${thisWindow.id}`);
  };

  const { formattedForecast: convertedForecast } = useFormatForecastData(
    location?.id
  );

  if (location && !location.enabled)
    return (
      <Grid
        item
        justifyContent='center'
        alignItems='center'
        container
        style={{ paddingBottom: '23%', paddingTop: '23%' }}
      >
        <Grid item>
          <CircularProgress />
        </Grid>
      </Grid>
    );
  if (
    (isLoading && (!thisWindow || !location)) ||
    (thisWindow?.forecast && !convertedForecast)
  )
    return (
      <Grid
        item
        justifyContent='center'
        alignItems='center'
        container
        style={{
          paddingBottom: '23%',
          paddingTop: '23%',
          paddingLeft: '1rem',
          paddingRight: '1rem',
        }}
      >
        <Grid item>
          <Typography style={{ color: grey[500] }}>
            Location not enabled.
          </Typography>
        </Grid>
      </Grid>
    );
  if (thisWindow?.location_id && !location) {
    return (
      <Grid
        item
        justifyContent='center'
        alignItems='center'
        container
        style={{
          paddingBottom: '23%',
          paddingTop: '23%',
          paddingLeft: '1rem',
          paddingRight: '1rem',
        }}
      >
        <Grid item>
          <Typography style={{ color: grey[500] }}>
            No weather data loaded. Data refreshes every 15 minutes.
          </Typography>
        </Grid>
      </Grid>
    );
  }
  if (!thisWindow || !location || (thisWindow.forecast && !convertedForecast)) {
    return (
      <Grid
        item
        justifyContent='center'
        alignItems='center'
        container
        style={{ paddingBottom: '23%', paddingTop: '23%' }}
      >
        <Grid item>
          <Typography style={{ color: grey[500] }}>
            {inEditMode
              ? 'Click to add weather conditions.'
              : 'Click Edit Wall to add weather conditions.'}
          </Typography>
        </Grid>
      </Grid>
    );
  } else {
    return (
      <Grid
        item
        container
        style={{ padding: '.5rem' }}
        onClick={handleWindowClicked}
      >
        <WeatherChart
          weatherData={
            thisWindow.forecast
              ? convertedForecast?.hourlyForecast
                ? convertedForecast.hourlyForecast
                : []
              : location.weather
          }
          measurement={thisWindow.measurement}
          locationName={location.name}
          durationLabel={
            thisWindow.forecast
              ? '(Forecast)'
              : '(' + thisWindow.range + ' ' + thisWindow.range_unit + ')'
          }
          spanGaps={!!thisWindow.forecast}
          wallId={wallId}
        />
      </Grid>
    );
  }
};

export default WeatherWindow;
